'use client'

import { Button, Chip as MuiChip, ListItemLayout, LuneTheme } from '@lune-fe/lune-ui-lib'
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined'
import type { SxProps } from '@mui/material'
import Box from '@mui/material/Box'
import Text from '@mui/material/Typography'
import Image from 'next/image'
import { FC } from 'react'
import CountUp from 'react-countup'

import Divider from '@/components/Divider'
import FullScreenOnMobileWrapper from '@/components/FullScreenOnMobileWrapper'
import TwoColumnLayout, { ColumnWrapOrder } from '@/components/TwoColumnLayout'
import useDevice from '@/hooks/useDevice'

const INTERVAL_SECONDS = 60

const CounterBox: FC<{
    adoption: string
    counterCb?: ({
        intervalSeconds,
    }: {
        intervalSeconds: number
    }) => { prev: number; current: number } | undefined
    usageFallback: string
}> = ({ adoption, counterCb, usageFallback }) => {
    const { mdScreen } = useDevice()
    const { palette } = LuneTheme
    const counters = counterCb ? counterCb({ intervalSeconds: INTERVAL_SECONDS }) : undefined

    return (
        <>
            <Box>
                <Text variant={'caption'} sx={{ color: palette.White }}>
                    Adoption
                </Text>
                <Text sx={{ color: palette.White }} variant={mdScreen ? 'h6' : 'h5'}>
                    {adoption}
                </Text>
            </Box>
            <Box>
                <Text variant={'caption'} sx={{ color: palette.White }}>
                    Usage
                </Text>
                <Text sx={{ color: palette.White }} variant={mdScreen ? 'h6' : 'h5'}>
                    {counters ? (
                        <CountUp
                            duration={INTERVAL_SECONDS}
                            start={counters.prev}
                            end={counters.current}
                            separator={','}
                            decimals={0}
                            suffix={' calculations'}
                        />
                    ) : (
                        <>{usageFallback}</>
                    )}
                </Text>
            </Box>
        </>
    )
}

const Chip: FC<{ label: string }> = ({ label }) => {
    const { smScreen } = useDevice()
    const { palette } = LuneTheme
    return (
        <MuiChip
            sx={{
                backgroundColor: 'unset',
                color: palette.White,
                borderRadius: '20px',
                background: 'rgba(255, 255, 255, 0.07)',
                height: '28px',
                ...(smScreen
                    ? {
                          fontSize: '12px',
                      }
                    : {}),
            }}
            label={label}
        />
    )
}

const CustomerCaseStudyBox: FC<{
    reference: string
    chipLabels: [string, string, string]
    mainText: string
    subText: string
    counterBox: {
        adoption: string
        counterCb?: ({
            intervalSeconds,
        }: {
            intervalSeconds: number
        }) => { prev: number; current: number } | undefined
        usageFallback: string
    }
    avatar: {
        title: string
        subtitle: string
    }
    images: {
        avatar: {
            source: string
            altText: string
        }
        desktop: {
            source: string
            altText: string
        }
        mobile: {
            source: string
        }
    }
    sx: SxProps
}> = ({ reference, chipLabels, mainText, subText, counterBox, avatar, images, sx }) => {
    const { mdScreen, smScreen } = useDevice()
    const { palette, spacing } = LuneTheme

    return (
        <FullScreenOnMobileWrapper>
            <TwoColumnLayout
                columnWrapOrderOnMobile={ColumnWrapOrder.REVERSE}
                sx={{
                    ...sx,
                    ...(smScreen
                        ? {
                              backgroundImage: `${images.mobile.source}`,
                              backgroundPosition: 'center',
                              backgroundSize: 'cover',
                              padding: `${spacing(60)} ${spacing(4)} ${spacing(10)} ${spacing(4)}`,
                          }
                        : {
                              borderRadius: '48px',
                              padding: `${spacing(10)}`,
                          }),
                }}
                alignItems="flex-start"
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: mdScreen ? 3 : 4,
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            gap: 1.5,
                            flexDirection: 'row',
                            flexWrap: 'wrap',
                        }}
                    >
                        <Chip label={chipLabels[0]} />
                        <Chip label={chipLabels[1]} />
                        <Chip label={chipLabels[2]} />
                    </Box>
                    <Text sx={{ color: palette.White }} variant={mdScreen ? 'h5' : 'h4'}>
                        {mainText}
                    </Text>
                    <Text sx={{ color: palette.White }} variant={mdScreen ? 'body2' : 'body1'}>
                        {subText}
                    </Text>
                    <ListItemLayout
                        sx={{
                            '.MuiListItemAvatar-root': {
                                maxWidth: '72px',
                                maxHeight: '72px',
                            },
                        }}
                        title={
                            <Text sx={{ color: palette.White }} variant={'h6'}>
                                {avatar.title}
                            </Text>
                        }
                        subTitle={
                            <Text sx={{ color: palette.White }} variant={'body3'}>
                                {avatar.subtitle}
                            </Text>
                        }
                        image={
                            <Image
                                src={images.avatar.source}
                                width={72}
                                height={72}
                                alt={images.avatar.altText}
                            />
                        }
                    />
                    <Divider sx={{ opacity: '0.1' }} />
                    <CounterBox
                        adoption={counterBox.adoption}
                        counterCb={counterBox.counterCb}
                        usageFallback={counterBox.usageFallback}
                    />
                    {smScreen && (
                        <Button
                            sx={{ width: '100%' }}
                            light={true}
                            variant={'contained'}
                            href={reference}
                            leftIcon={<ArrowForwardOutlinedIcon />}
                        >
                            Read the case study
                        </Button>
                    )}
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: mdScreen ? 3 : 9,
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            width: '100%',
                            height: 'auto',
                            position: 'relative',
                        }}
                    >
                        {smScreen ? (
                            <></>
                        ) : (
                            <Image
                                src={images.desktop.source}
                                width={480}
                                height={480}
                                alt={images.desktop.altText}
                                style={{
                                    objectFit: 'cover',
                                    objectPosition: 'top',
                                    width: '100%',
                                    borderRadius: '24px',
                                }}
                            />
                        )}
                    </Box>
                    {!smScreen && (
                        <Button
                            sx={{ width: '100%' }}
                            light={true}
                            variant={'contained'}
                            href={reference}
                            leftIcon={<ArrowForwardOutlinedIcon />}
                        >
                            Read the case study
                        </Button>
                    )}
                </Box>
            </TwoColumnLayout>
        </FullScreenOnMobileWrapper>
    )
}

export default CustomerCaseStudyBox
